import { createContext, useCallback, useContext, useEffect, useState, ReactNode } from 'react';
import type { User, Session } from '@supabase/supabase-js';
import { supabase } from '@/integrations/supabase/client';
import { isExpaiApiEnabled } from '@/integrations/api/config';
import * as apiAuth from '@/integrations/api/auth_api';
import { getAccessToken } from '@/integrations/api/token_storage';
import { setSentryUser, clearSentryUser } from '@/lib/sentry';
import { track, resetAnalyticsUser } from '@/lib/analytics';

type AppRole = 'owner' | 'admin' | 'supervisor' | 'operator';

interface Profile {
  id: string;
  full_name: string | null;
  avatar_url: string | null;
  department: string | null;
  shift: string | null;
}

/** Minimal user shape used across the app (Supabase User or API-backed). */
export interface AuthUser {
  id: string;
  email?: string;
}

interface AuthContextType {
  user: AuthUser | null;
  session: Session | null;
  profile: Profile | null;
  role: AppRole | null;
  loading: boolean;
  mfaEnrolled: boolean;
  mustEnrollMfa: boolean;
  refreshMfaState: () => Promise<void>;
  refreshAuth: () => Promise<void>;
  signOut: () => Promise<void>;
  hasRole: (role: AppRole) => boolean;
  canAccess: (requiredRoles: AppRole[]) => boolean;
  useApiAuth: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

const MFA_REQUIRED_ROLES: AppRole[] = ['owner', 'admin'];

// Existing users grandfathered out of the forced TOTP enrollment (they can still
// enrol voluntarily from Account → Security). New owner/admin users are NOT
// exempt and must enrol. Lowercase emails.
const MFA_EXEMPT_EMAILS = new Set<string>([
  'admin@expai.eu',
  'hakel122003@gmail.com',
  'jose.reinoza@nextgeneration.eu.com',
  'diego.castellanos@nextgeneration.eu.com',
  'keunpil.roh@nanosys.kr',
  'josemrd1994@gmail.com',
  // EXCEPCIÓN TEMPORAL (petición de Emilio, oct 2026): Ali revisa el código
  // del portal y el TOTP le bloqueaba el acceso. Retirar esta línea cuando
  // termine su revisión — la decisión del 25/09 (TOTP obligatorio para
  // owner/admin) sigue vigente para el resto.
  'alik@acd.com.tr',
]);

function applyApiUser(data: apiAuth.ApiUser) {
  const profile: Profile | null = data.profile
    ? {
        id: data.profile.id,
        full_name: data.profile.full_name,
        avatar_url: data.profile.avatar_url,
        department: data.profile.department,
        shift: data.profile.shift,
      }
    : null;
  return {
    user: { id: data.id, email: data.email } as AuthUser,
    profile,
    role: data.role,
    mfaEnrolled: data.mfa_enrolled,
    isDemo: data.tenant?.slug === 'demo',
  };
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const useApiAuth = isExpaiApiEnabled();
  const [user, setUser] = useState<AuthUser | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [profile, setProfile] = useState<Profile | null>(null);
  const [role, setRole] = useState<AppRole | null>(null);
  const [loading, setLoading] = useState(true);
  const [mfaEnrolled, setMfaEnrolled] = useState(false);
  const [isDemo, setIsDemo] = useState(false);

  const fetchMfa = useCallback(async () => {
    if (useApiAuth) {
      try {
        const { totp } = await apiAuth.listMfaFactors();
        setMfaEnrolled(totp.some((f) => f.status === 'verified'));
      } catch {
        setMfaEnrolled(false);
      }
      return;
    }
    try {
      const { data } = await supabase.auth.mfa.listFactors();
      const totp = (data?.totp ?? []) as Array<{ status?: string }>;
      setMfaEnrolled(totp.some((f) => f.status === 'verified'));
    } catch {
      setMfaEnrolled(false);
    }
  }, [useApiAuth]);

  const fetchUserData = useCallback(
    async (userId: string) => {
      if (useApiAuth) {
        try {
          const { user: apiUser } = await apiAuth.fetchMe();
          const applied = applyApiUser(apiUser);
          setUser(applied.user);
          setProfile(applied.profile);
          setRole(applied.role);
          setMfaEnrolled(applied.mfaEnrolled);
          setIsDemo(applied.isDemo);
          setSentryUser(apiUser.id);
        } catch {
          setUser(null);
          setProfile(null);
          setRole(null);
        }
        return;
      }

      const [profileRes, roleRes, tenantRes] = await Promise.all([
        supabase.from('profiles').select('*').eq('id', userId).single(),
        supabase.from('user_roles').select('role').eq('user_id', userId).single(),
        (
          supabase.from('tenant_users' as never).select('tenants(slug)') as unknown as Promise<{
            data: Array<{ tenants: { slug: string } | null }> | null;
          }>
        ).then((r) => r),
      ]);

      if (profileRes.data) setProfile(profileRes.data);
      if (roleRes.data) setRole(roleRes.data.role as AppRole);

      const slugs = (tenantRes.data ?? [])
        .map((row) => row.tenants?.slug)
        .filter((s): s is string => !!s);
      setIsDemo(slugs.includes('demo'));

      await fetchMfa();
    },
    [fetchMfa, useApiAuth],
  );

  useEffect(() => {
    if (useApiAuth) {
      const token = getAccessToken();
      if (!token) {
        setLoading(false);
        return;
      }
      void fetchUserData('').finally(() => setLoading(false));
      return;
    }

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange(async (_event, nextSession) => {
      setSession(nextSession);
      setUser(nextSession?.user ?? null);

      if (nextSession?.user) {
        setSentryUser(nextSession.user.id);
        setTimeout(() => fetchUserData(nextSession.user.id), 0);
      } else {
        clearSentryUser();
        setProfile(null);
        setRole(null);
        setMfaEnrolled(false);
        setIsDemo(false);
      }
      setLoading(false);
    });

    supabase.auth.getSession().then(({ data: { session: nextSession } }) => {
      setSession(nextSession);
      setUser(nextSession?.user ?? null);
      if (nextSession?.user) {
        setSentryUser(nextSession.user.id);
        fetchUserData(nextSession.user.id);
      }
      setLoading(false);
    });

    return () => subscription.unsubscribe();
  }, [fetchUserData, useApiAuth]);

  const signOut = async () => {
    track('logout');
    if (useApiAuth) {
      await apiAuth.logout();
    } else {
      await supabase.auth.signOut();
    }
    clearSentryUser();
    resetAnalyticsUser();
    setUser(null);
    setSession(null);
    setProfile(null);
    setRole(null);
    setMfaEnrolled(false);
    setIsDemo(false);
  };

  const hasRole = (r: AppRole) => role === r;
  const canAccess = (requiredRoles: AppRole[]) => {
    if (!role) return false;
    return requiredRoles.includes(role);
  };

  const bypass = import.meta.env.VITE_E2E_BYPASS_MFA === 'true';
  const mfaExempt = !!user && MFA_EXEMPT_EMAILS.has((user.email ?? '').toLowerCase());
  const mustEnrollMfa =
    !!user &&
    !!role &&
    MFA_REQUIRED_ROLES.includes(role) &&
    !mfaEnrolled &&
    !isDemo &&
    !bypass &&
    !mfaExempt;

  return (
    <AuthContext.Provider
      value={{
        user,
        session,
        profile,
        role,
        loading,
        mfaEnrolled,
        mustEnrollMfa,
        refreshMfaState: fetchMfa,
        refreshAuth: () => fetchUserData(user?.id ?? ''),
        signOut,
        hasRole,
        canAccess,
        useApiAuth,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within AuthProvider');
  return context;
}
